import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import {
  searchText,
  submitText,
  firstNameText,
  lastNameText,
  cancelText,
  selectedUsersText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';

/**
 * Form component that allows bulk associating of users to a unit
 * @param {Function} props.onSubmit - Function to be performed when the form is submitted
 * @param {array} props.userArray - Array of all of the users
 * @param {string} props.unit - The id for the unit that the users will be associated with
 * @param {array} props.users - Any users that have been preselected elsewhere and passed to prepopulate the selected users table
 */
export function UnconnectedAssociateUsersToUnitForm(props) {
  const { onSubmit, userArray, users, unit } = props;
  const [usersFilter, setUsersFilter] = useState('');

  const [showSelectedUsersHelpText, setShowSelectedUsersHelpText] = useState(
    false
  );
  const selectedUsersHelpTextRef = useRef();
  function toggleSelectedUsersHelpText(value) {
    setShowSelectedUsersHelpText(value);
  }
  const selectedUsersHelpText = (
    <FormattedMessage
      id="associateUsersToUnitForm.selectedUsersHelpText"
      defaultMessage="The users that have been selected. To remove a user from this list, click the 'x' icon. To add a user, click in the check box."
      description="Help text."
    />
  );

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const usersSearchFilter = (event) => {
    const value = event.target.value;
    setUsersFilter(value);
  };

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const usersTable = (values) => {
    // map over the users and put into the format that is required for the table component
    const initialFilterList = userArray.filter((u) => {
      if (u.units) {
        if (u.units.includes(unit)) {
          return false;
        } else if (values.usersId.includes(u.id)) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    });

    const usersArray = initialFilterList.map((user) => {
      let checked = null;
      if (users) {
        checked = users.includes(user.id) ? true : null;
      }
      return {
        selector: {
          checked: checked,
          sortBy: user.id,
          display: user.id,
        },
        firstName: {
          sortBy: user.first_name.toUpperCase(),
          display: `${user.first_name} ${
            user.preferred_name ? `"${user.preferred_name}"` : ''
          }`,
        },
        lastName: {
          sortBy: user.last_name.toUpperCase(),
          display: `${user.last_name} ${user.suffix ? `"${user.suffix}"` : ''}`,
        },
      };
    });

    let searchedUsers = [];
    // Take the array and filter based on the text entered in the search bar.
    if (usersFilter !== '') {
      searchedUsers = usersArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const fname = item.firstName.display.toUpperCase();
        const lname = item.lastName.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (
          fname.includes(usersFilter.toUpperCase()) ||
          lname.includes(usersFilter.toUpperCase())
        ) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedUsers = usersArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'firstName',
        display: firstNameText,
      },
      {
        isSortable: true,
        sortBy: 'lastName',
        display: lastNameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="usersId"
        selectorParamValue="id"
        sortParam="lastName"
        tableData={searchedUsers}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__form-selector-table table_associate-users-units--users"
        // selected={parent_unit}
        data-test="associate-users-to-unit-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedUsersTable = (values) => {
    let table;
    let usersId = values.usersId;
    let userRows;
    if (usersId.length > 0) {
      if (usersId) {
        const filteredUsers = usersId.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(usersId);
          }
          const userId = userArray.filter((user) => user.id === thisId);
          return userId[0];
        });
        userRows = filteredUsers.map((user) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="usersId"
                component="input"
                type="checkbox"
                value={user.id}
              />
              <SvgXIcon />
            </label>
          );
          return {
            button: {
              sortBy: '',
              display: removeButton,
            },
            firstName: {
              sortBy: user.first_name.toUpperCase(),
              display: `${user.first_name} ${
                user.preferred_name ? `"${user.preferred_name}"` : ''
              }`,
            },
            lastName: {
              sortBy: user.last_name.toUpperCase(),
              display: `${user.last_name} ${
                user.suffix ? `"${user.suffix}"` : ''
              }`,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'firstName',
          display: firstNameText,
        },
        {
          isSortable: true,
          sortBy: 'lastName',
          display: lastNameText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="lastName"
          tableData={userRows}
          tableStyles="table__form-selector-table table_associate-users-units--users"
          data-test="associate-users-to-unit-selected-user-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  return (
    <React.Fragment>
      <div className="associate-users-units__message-container" />
      <Form
        data-test="associate-users-to-unit-form"
        onSubmit={onSubmit}
        initialValues={{
          usersId: users || [],
          unit: unit || '',
          initialUsers: users || [],
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="unit"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="initialUsers"
              component="input"
              type="select"
            />
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="usersId">
                    {selectedUsersText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedUsersHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedUsersHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedUsersHelpTextRef}
                toggleFunctionction={toggleSelectedUsersHelpText}
                message={selectedUsersHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              {selectedUsersTable(values)}

              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="usersFilter"
                  name="usersFilter"
                  onChange={usersSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={usersFilter}
                />
              </div>

              {usersTable(values)}
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={props.previous}
                  data-test="associate-users-to-unit-form-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  const unitArray = Object.values(state.units.units);
  const userArray = Object.values(state.users.users);

  return {
    unitArray,
    userArray,
  };
};
export default connect(mapStateToProps)(UnconnectedAssociateUsersToUnitForm);
